const MOUSE_MOVE_THRESHOLD = 5;
const MIN_SIZE_NEW_SHAPES = 10;
const LINE_HEIGHT = 1.3;
const FOCUSED_ITEM_KEY = "focused";
const SET_POSITION_KEY = "set-position";
const SNAPPING_DISTANCE = 16;
const MIN_FONT_SIZE = 1;
const MAX_FONT_SIZE = 999;
const DEFAULT_BLACK = "#081B2F";

const MIN_WIDTH_TO_SHOW_PAYMENT_PAGE = 1440;

const STICKY_NOTE_COLORS = ["#FFD9CD", "#FFE7BC", "#BFEDDC", "#BFDCFF", "#E3D1FE", "#FFE1E9", "#E5E8EA", "#FFFFFF"];
const CIM_STICKY_NOTE_COLORS = [
  "#FFD3EE",
  "#CFBEFA",
  "#B2D9FB",
  "#C1F2EE",
  "#C0EDC1",
  "#F3B2A6",
  "#F8D5AE",
  "#FAE3A2",
  "#E6E6E6",
  "#FFFFFF",
];
const COLOR_PALETTE = [
  "#081B2F",
  "#E03E1A",
  "#F5B53D",
  "#00B875",
  "#1973FF",
  "#7549F5",
  "#E9478B",
  "#A5AAAD",
  "#F38528",
  "#FFD748",
  "#9FDA8B",
  "#57CEF3",
  "#C883FE",
  "#FFB4C9",
];

const CIM_COLOR_PALETTE = [
  "#F4B7C9",
  "#7F52F6",
  "#41A0FF",
  "#78CBEF",
  "#83D27F",
  "#E05634",
  "#F1A255",
  "#F7CB5D",
  "#0D1B2D",
  "#A6AAAD",
];

const LIGHT_VARIANT: Record<string, string> = {
  "#081B2F": "#9098A1",
  "#E03E1A": "#EF9E8C",
  "#F5B53D": "#F8CF81",
  "#00B875": "#80DBBA",
  "#1973FF": "#8CB9FF",
  "#7549F5": "#BAA4FA",
  "#E9478B": "#F4A3C5",
  "#A5AAAD": "#C4C8CA",
  "#F38528": "#F7AE97",
  "#FFD748": "#FFD748",
  "#9FDA8B": "#B7E3A8",
  "#57CEF3": "#9AE2F8",
  "#C883FE": "#E3C1FE",
  "#FFA6BF": "#FFBFD1",
};

const LIGHTER_VARIANT: Record<string, string> = {
  "#081B2F": "#F4F7FA",
  "#E03E1A": "#FDF0ED",
  "#F5B53D": "#FEF8EC",
  "#00B875": "#E6F8F1",
  "#1973FF": "#E6F1FF",
  "#7549F5": "#F1EDFE",
  "#E9478B": "#FDEDF3",
  "#A5AAAD": "#F4F7FA",
  "#F38528": "#FEF4EC",
  "#FFD748": "#FFFBEB",
  "#9FDA8B": "#F3FAF0",
  "#57CEF3": "#EEFAFE",
  "#C883FE": "#FAF3FF",
  "#FFA6BF": "#FFF4F7",
};

const TEAM_PICKER_COLOR_PALETTE = [
  "#F5B53D",
  "#FFB4C9",
  "#00B875",
  "#7549F5",
  "#57CEF3",
  "#E9478B",
  "#F38528",
  "#FFD748",
  "#C883FE",
  "#9FDA8B",
];
const COLOR_PALETTE_INCLUDING_WHITE_TRANSPARENT = [
  ...COLOR_PALETTE.slice(0, 7),
  "white",
  ...COLOR_PALETTE.slice(7),
  "transparent",
];

const MINDMAP_COLOR_PALETTE = [
  "#1973FF",
  "#7549F5",
  "#E9478B",
  "#E34D2A",
  "#F38528",
  "#F5B53D",
  "#FFD748",
  "#9FDA8B",
  "#00B875",
];

const ORG_CHART_COLOR_PALETTE = [
  "#081B2F",
  "#E03E1A",
  "#F5B53D",
  "#00B875",
  "#1973FF",
  "#7549F5",
  "#E9478B",
  "#A5AAAD",
  "#F38528",
  "#FFD748",
  "#9FDA8B",
  "#57CEF3",
  "#C883FE",
  "#FFA6BF",
];

const MENU_ITEMS = {
  SHAPE: "shape",
  CONNECTOR: "connector",
  TEXT_BLOCK: "text-block",
  MINDMAP: "mindmap",
  PEN: "pen",
  STICKY_NOTE: "sticky-note",
  FRAME: "frame",
  Hand: "hand",
};

const CANVAS_ELEMENTS = {
  SHAPE: "shape",
  CONNECTOR: "connector",
  TEXT_BLOCK: "textBlock",
  DRAWING: "drawing",
  STICKY_NOTE: "stickyNote",
  FILE: "file",
  TASK_CARD: "taskCard",
  TASK: "task",
  MONDAY_TASK: "mondayTask",
  FRAME: "frame",
  COMMENT: "comment",
  MINDMAP: "mindmap",
  MINDMAP_ORG_CHART: "mindmapOrgChart",
  ORG_CHART: "orgChartRoot",
  ORG_CHART_NODE: "orgChartNode",
  INTEGRATION: "integrationItem",
  CARD_STACK: "cardStack",
  LIVE_INTEGRATION: "liveIntegration",
  TIMELINE: "timeline",
  TABLE: "table",
  GANTT: "gantt",
  TABLE_CELL: "tableCell",
  NOUN_ICON: "nounIcon",
  CHART: "chart",
  ICON: "icon",
  PDF: "pdf",
} as const;

type TypeCanvasElement = typeof CANVAS_ELEMENTS[keyof typeof CANVAS_ELEMENTS];

type LegacyShapeTypes = "circle" | "rect" | "rect-rounded" | "triangle" | "diamond" | "hexagon";
export type ExtendedCanvasElement = TypeCanvasElement | LegacyShapeTypes;

const ELEMENT_TRAITS = {
  COLOR: "color",
  STROKE_COLOR: "stroke-color",
  LINE_WIDTH: "line-width",
  CONNECTOR_TYPE: "line-type",
  SHAPE_TYPE: "shape-type",
  ENDPOINT_TYPE: "endpoint-type",
  TEXT_SIZE: "text-size",
  TEXT_ALIGN: "text-align",
  TEXT_STYLE: "text-style",
  TEXT_COLOR: "text-color",
};

const TEXT_SIZES = {
  LARGE: "large",
  MEDIUM: "medium",
  SMALL: "small",
};

const CONTEXT_MENU_ELEMENT = {
  COPY: "Copy",
  CUT: "Cut",
  DELETE: "Delete",
  ADD_LINK: "Link to...",
  EDIT_LINK: "Edit link",
  LOCK: "Lock",
  UNLOCK: "Unlock",
  MOVE_TO_FRONT: "Bring to front",
  MOVE_FORWARD: "Move forward",
  MOVE_BACKWARD: "Move backward",
  MOVE_TO_BACK: "Send to back",
  GROUP: "Group",
  UNGROUP: "Ungroup",
  FLIP_HORIZONTALLY: "Flip horizontally",
  FLIP_VERTICALLY: "Flip vetically",
  SET_AS_THUMBNAIL: "Set as thumbnail",
  EXPORT: "Export",
  EXPORT_IMAGE: "Export as Image",
  EXPORT_PDF: "Export as PDF",
  CLEAR_THUMBNAIL: "Clear thumbnail",
  CREATE_MONDAY_ITEM: "Create monday item",
  CREATE_MONDAY_SUBMENU_ITEM: "Create item",
  CREATE_MONDAY_SUBMENU_SUBITEM: "Create subitem",
  ADD_GANTT_TASK: "Add task",
  SHOW_DATA_LAYER_EDITOR: "Edit data",
};

const CONTEXT_MENU_STAGE = {
  PASTE: "Paste",
  ZOOM_IN: "Zoom in",
  ZOOM_OUT: "Zoom out",
  ADD_TEXT: "Add text",
  ADD_STICKY: "Add sticky note",
  ADD_SHAPE: "Add shape",
  ADD_MINDMAP: "Add mind map",
  ADD_COMMENT: "Add comment",
};

const WIDTH_SIZES = {
  LARGE: "large",
  SMALL: "small",
};

const SHAPES = {
  RECT: "rect",
  RECT_ROUNDED: "rect-rounded",
  DIAMOND: "diamond",
  CIRCLE: "circle",
  TRIANGLE: "triangle",
  HEXAGON: "hexagon",
} as const;

type TypeShape = typeof SHAPES[keyof typeof SHAPES];

const DEFAULTS = {
  STICKY_NOTE_MAX_FONT_SIZE: 144,
  TEXT: "Add text",
  CARD_TEXT: "Type something...",
  CARD_WIDTH: 325,
  CARD_HEIGHT: 160,
  MONDAY_CARD_WIDTH: 482, // width is fixed for all cards
  MONDAY_CARD_HEIGHT: 214, // minimum height for monday card. can be more
  FONT: "Poppins",
  FONTSIZE: 14,
  ROUNDED_RECT_RADIUS: 15,
  TEXT_ALIGN: "center",
  TEXT_COLOR: DEFAULT_BLACK,
  TEXT_PLACEHOLDER_COLOR: "#848199",
  MINDMAP_ROOT_STROKE_COLOR: "#113255",
  MINDMAP_PLACEHOLDER: "Type something",
  MINDMAP_NODE_VERTICAL_PADDING: 20,
  MINDMAP_NODE_HORIZONTAL_PADDING: 8,
  MINDMAP_COLOR: "#1973FF",
  MINDMAP_CONNECTOR_CHILD_PADDING: 15,
  MINDMAP_ADD_NODE_BUTTON_PADDING: 40,
  NEW_TABLE_TITLE: "New Table",
};

const TRACKING_CATEGORY = {
  CANVAS_ACTION: "canvas-action",
  ADMIN_ACTION: "admin-actions",
  SIDE_MENU: "side-menu",
  USER_PRESENCE: "user-presence",
  BILLING: "billing",
  ACQUISITION: "acquisition",
  PAGE_VIEWS: "page-views",
  TEMPLATES: "templates",
  CONNECTIVITY: "connectivity",
  SIGNUP: "signup",
  ONBOARDING: "onboarding",
  FILES: "files",
  ACCEPT_INVITATION: "accept_invitation",
  WALKTHROUGH: "walkthrough",
  COLLABORATION: "collaboration",
  INTEGRATIONS: "integrations",
  TEAM_ACTION: "team-action",
  PROJECT_ACTION: "project-action",
  VIDEO_CALLS: "video-calls",
  ORG_CHART: "org-chart",
  VOTING: "voting",
  INVITE_ACTION: "invite-action",
  RESTRICTED_ACTION: "restricted-action",
  MIRO_MIGRATION: "miro-migration",
  SEARCH_BAR: "searchbar",
  PASSWORD_PROTECTED: "password-protected",
  AI_GENERATOR: "ai-generator",
};

const GA_CONVERSION_TYPES = {
  HARD_SIGNUP: "hard-signup",
  UPGRADE_CTA: "upgrade-cta",
  NEW_ITEM_PLACED: "new-item-placed",
  INVITE_LINK_COPIED: "invite-link-copied",
  CANVAS_LIMIT_REACHED: "canvas-limit-reached",
  RETURNING_USER: "returning-user",
  PROMOTION_CLICKED: "promotion-clicked",
  PAID_PLAN_SELECTED: "paid-plan-selected",
  PAYMENT_COMPLETED: "payment-completed",
  TEAM_SIGNUP: "team-signup",
};

const PRESENT_USER_THRESHOLD_IN_SEC = 5 * 60;
const IDLE_USER_THRESHOLD_IN_SEC = 2 * 60;
const MAX_ONLINE_BOARD_USERS = 10;

export enum Plan {
  basic = 1,
  pro = 2,
  team = 3,
  cim_basic = 4,
  cim_pro = 5,
}

const COOKIE_NAMES = {
  CANVAS_PLAN_ID: "canvas_plan_id",
  BOARD_TEMPLATE: "canvas_board_template",
  SOURCE: "source",
  SESSION: "connect.sid",
};

const MIN_ELEMENTS_TO_OPTIMIZE_DRAG = 10;

const SESSION_STORAGE_KEYS = {
  FIRST_ACCESS: "first-access",
  SHOW_TEMPLATES_ON_START: "show-templates-on-start",
  SHOW_ORG_CHART_TEMPLATES_ON_START: "show-org-chart-templates-on-start",
  TEMPLATE_CHOSEN_OPTIONS: "template-options",
  CREATED_TEMPLATE_DATA: "created-template-data",
  CREATED_TEMPLATE_LINK_DATA: "created-template-link-data",
  AUTH_SESSION_ID: "auth-session-id",
};

const STAGE_ZOOM = {
  MIN: 0.01,
  MAX: 4,
};
const ZOOM_STEP = 35;
const STAGE_ZOOM_VELOCITY_LIMIT = 30;
const STAGE_PAN_FACTOR = 1.35;

const INDEXED_DB = {
  OBJECT_STORES: {
    FILES_PREVIEWS: "files-previews",
  },
  INDEXEDS: {
    UPPY_FILE_ID: "uppyFileId",
  },
};

const PRICING = {
  PRO: {
    VALUE: 10,
  },
};

const MIN_STRIPE_ALLOWED_AMOUNT = 0.5; // Minimum allowed amount in stripe is $0.5 (50 cents)

const MAIL_TRIGGERS = {
  UPGRADE_PLAN: "start_checkout",
};

const ALL_TEMPLATES_TAG_ID = -1;
const MY_TEMPLATES_TAG_ID = -2;
const ACCOUNT_TEMPLATES_TAG_ID = -3;

const INTEGRATIONS_ITEMS_LIMIT = 30;
const INTEGRATIONS_ITEMS_QUERY_LIMIT = 50;

const TRIAL_DAYS_DEFAULT_PERIOD = 14;

export type { TypeCanvasElement, TypeShape };

export const MIN_PAID_SEATS = 3;

export const FLOWCHART_SUPPORTED_ELEMENTS = [
  CANVAS_ELEMENTS.STICKY_NOTE,
  CANVAS_ELEMENTS.SHAPE,
  CANVAS_ELEMENTS.ICON,
  SHAPES.CIRCLE,
  SHAPES.DIAMOND,
  SHAPES.HEXAGON,
  SHAPES.RECT,
  SHAPES.RECT_ROUNDED,
  SHAPES.TRIANGLE,
];

export const SNAPPING_EXCLUDED_ELEMENTS: string[] = [
  CANVAS_ELEMENTS.ORG_CHART,
  CANVAS_ELEMENTS.ORG_CHART_NODE,
  CANVAS_ELEMENTS.TIMELINE,
  CANVAS_ELEMENTS.GANTT,
];

const URL_QUERY_PARAMS = {
  SETUP_INTENT: "setup_intent",
};

export const CACHE_EXPIRY_MS = 5 * 60 * 1000; // 5 minutes

export const TEAMS_AND_PROJECTS_LOCAL_STORAGE_KEY = "selectedTeamsAndProjects";
export type SelectedTeamsAndProjects = {
  selectedTeam?: string;
  selectedProject?: string;
}[];

const WORKCANVAS_PRODUCT_KIND = "whiteboard";

const ELEMENTS_SUPPORTING_DATA_LAYER: TypeCanvasElement[] = [
  CANVAS_ELEMENTS.SHAPE,
  CANVAS_ELEMENTS.CHART,
  CANVAS_ELEMENTS.STICKY_NOTE,
];

const ELEMENTS_SUPPORTING_CUSTOM_FIELDS: TypeCanvasElement[] = [CANVAS_ELEMENTS.SHAPE, CANVAS_ELEMENTS.STICKY_NOTE];

const AI_EDITS_ALLOWED_ELEMENT_TYPES: TypeCanvasElement[] = [
  CANVAS_ELEMENTS.TEXT_BLOCK,
  CANVAS_ELEMENTS.STICKY_NOTE,
  CANVAS_ELEMENTS.SHAPE,
];

// region: canvas in monday
const MONDAY_APP_SESSION_TOKEN = "x-monday-app-session-token" as const;
const CANVAS_AUTH_TOKEN = "x-canvas-auth-token" as const;
const SESSION_ID_HEADER = "x-session-id" as const;
// endregion

export const ACCOUNT_LIMITS = {
  DEFAULT: 10_000,
  BOARDS: 30_000,
  CONVERT_TO_MONDAY_ITEMS: 30,
};

const MIN_DOWNLOADS_COUNT_TO_SHOW_DOWNLOADS_COUNT = 100;

const MIN_SCREEN_HEIGHT_FOR_WALKTHROUGH = 400;

export const CANVAS_STAGE_LAYERS = {
  OVERLAY: "Overlay",
  OVERLAY_ELEMENT_BOUNDS: "Overlay-element-bounds",
  ELEMENTS: "Elements",
};

const BOARD_ACCOUNT_REGION_HEADER = "x-board-account-id" as const;

export default {
  MENU_ITEMS,
  CANVAS_ELEMENTS,
  ELEMENT_TRAITS,
  TEXT_SIZES,
  CONTEXT_MENU_ELEMENT,
  CONTEXT_MENU_STAGE,
  WIDTH_SIZES,
  SHAPES,
  DEFAULTS,
  PRESENT_USER_THRESHOLD_IN_SEC,
  IDLE_USER_THRESHOLD_IN_SEC,
  MAX_ONLINE_BOARD_USERS,
  TRACKING_CATEGORY,
  GA_CONVERSION_TYPES,
  COOKIE_NAMES,
  MIN_ELEMENTS_TO_OPTIMIZE_DRAG,
  SESSION_STORAGE_KEYS,
  STAGE_ZOOM,
  ZOOM_STEP,
  STAGE_ZOOM_VELOCITY_LIMIT,
  STAGE_PAN_FACTOR,
  MOUSE_MOVE_THRESHOLD,
  MIN_SIZE_NEW_SHAPES,
  LINE_HEIGHT,
  INDEXED_DB,
  STICKY_NOTE_COLORS,
  CIM_STICKY_NOTE_COLORS,
  COLOR_PALETTE,
  CIM_COLOR_PALETTE,
  LIGHT_VARIANT,
  LIGHTER_VARIANT,
  TEAM_PICKER_COLOR_PALETTE,
  COLOR_PALETTE_INCLUDING_WHITE_TRANSPARENT,
  MINDMAP_COLOR_PALETTE,
  ORG_CHART_COLOR_PALETTE,
  FOCUSED_ITEM_KEY,
  SNAPPING_DISTANCE,
  SET_POSITION_KEY,
  PRICING,
  MAIL_TRIGGERS,
  MIN_FONT_SIZE,
  MAX_FONT_SIZE,
  ALL_TEMPLATES_TAG_ID,
  MY_TEMPLATES_TAG_ID,
  ACCOUNT_TEMPLATES_TAG_ID,
  INTEGRATIONS_ITEMS_LIMIT,
  INTEGRATIONS_ITEMS_QUERY_LIMIT,
  MIN_WIDTH_TO_SHOW_PAYMENT_PAGE,
  TRIAL_DAYS_DEFAULT_PERIOD,
  FLOWCHART_SUPPORTED_ELEMENTS,
  SNAPPING_EXCLUDED_ELEMENTS,
  MIN_STRIPE_ALLOWED_AMOUNT,
  URL_QUERY_PARAMS,
  WORKCANVAS_PRODUCT_KIND,
  MONDAY_APP_SESSION_TOKEN,
  CANVAS_AUTH_TOKEN,
  SESSION_ID_HEADER,
  ELEMENTS_SUPPORTING_DATA_LAYER,
  ELEMENTS_SUPPORTING_CUSTOM_FIELDS,
  AI_EDITS_ALLOWED_ELEMENT_TYPES,
  ACCOUNT_LIMITS,
  MIN_DOWNLOADS_COUNT_TO_SHOW_DOWNLOADS_COUNT,
  MIN_SCREEN_HEIGHT_FOR_WALKTHROUGH,
  BOARD_ACCOUNT_REGION_HEADER,
};
